import React, { useMemo, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, Control } from "react-hook-form";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  Tooltip,
} from "@heroui/react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { PiWarningFill, PiCheckSquareFill } from "react-icons/pi";
import { GiSandsOfTime } from "react-icons/gi";
import { CgUnavailable } from "react-icons/cg";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";

interface Product {
  product_id?: {
    value: string;
  };
  product_name?: {
    value: string;
  };
  product_specific_candidate?: {
    value: string;
    valueConfidence: number;
  };
  product_pageNumber?: {
    value: number;
  };
}

interface SpecificProduct {
  product_id: string;
  product_id_specific: string;
  referential_id_specific?: string;
  source: "hitl" | "bdd";
}

interface OrderData {
  order?: {
    products?: Product[];
    metadata?: {
      customer_id?: {
        value: string;
      };
    };
  };
}

interface APIPayload {
  operation: string;
  delete_before: boolean;
  data: Array<{
    referential_id_specific: string;
    product_id: string;
    product_id_specific: string;
    source?: string;
  }>;
}

interface ThirdPartyReferenceProps {
  data: OrderData;
  control: Control<any>;
  setValue: (name: string, value: any, options?: any) => void;
  getValues?: (name?: string) => any;
  onSubmit?: (payload: APIPayload) => void;
  onHoveredShapeId?: (action: string, canvasId: number | string) => void;
  hoveredShapeDraw?: {
    action: string;
    canvasId: string | number;
  };
  currentPage?: number;
}

export default function ThirdPartyReference({
  data,
  control,
  setValue,
  getValues,
  onSubmit,
  onHoveredShapeId,
  hoveredShapeDraw,
  currentPage
}: Readonly<ThirdPartyReferenceProps>) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const [renderKey, setRenderKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const customerId = useMemo(() => {
    return data?.order?.metadata?.customer_id?.value || "";
  }, [data]);

  const { data: specificProductsData, isLoading: isLoadingSpecificProducts } = useQuery({
    queryKey: ["specificProducts", customerId],
    queryFn: async () => {
      if (!customerId) return [];
      const response = await axiosPrivate.get(`/products/specific/bulk?customer_id=${customerId}&source=all`);
      return response.data || [];
    },
    enabled: !!customerId,
  });

  const specificProductsIndex = useMemo(() => {
    const index = new Map<string, { value: string; source: string }>();

    if (!specificProductsData || !Array.isArray(specificProductsData)) {
      return index;
    }

    for (const resultArray of specificProductsData) {
      if (Array.isArray(resultArray)) {
        for (const item of resultArray) {
          const existingEntry = index.get(item.product_id);

          const shouldSet = item.source === "hitl" || (item.source === "bdd" && !existingEntry);

          if (shouldSet) {
            index.set(item.product_id, {
              value: item.product_id_specific,
              source: item.source,
            });
          }
        }
      }
    }

    return index;
  }, [specificProductsData]);

  const findBestReference = useCallback((productId: string, productSpecificCandidate?: string): string => {
    const indexedEntry = specificProductsIndex.get(productId);
    if (indexedEntry) {
      return indexedEntry.value;
    }

    return productSpecificCandidate || "";
  }, [specificProductsIndex]);

  const getReferenceSource = useCallback((productId: string): string | undefined => {
    const indexedEntry = specificProductsIndex.get(productId);
    return indexedEntry?.source;
  }, [specificProductsIndex]);

  useEffect(() => {
    if (!data?.order?.products || isLoadingSpecificProducts) {
      setIsInitialized(false);
      return;
    }

    for (let index = 0; index < data.order.products.length; index++) {
      const product = data.order.products[index];
      const productId = product.product_id?.value || "";
      const productSpecificCandidate = product.product_specific_candidate?.value;
      const fieldName = `thirdPartyReference-${index}`;

      const currentValue = getValues?.(fieldName);

      if (currentValue === undefined || currentValue === null || currentValue === "") {
        const bestReference = findBestReference(productId, productSpecificCandidate);
        setValue(fieldName, bestReference);
      } 
    }

    setIsInitialized(true);
  }, [data, specificProductsData, findBestReference, setValue, getValues, isLoadingSpecificProducts]);

  const setHoveredShapeId = (action: string, canvasId: number | string) => {
    if (onHoveredShapeId) {
      onHoveredShapeId(action, canvasId);
    }
  };

  const getBorderClass = useCallback((confidence: number | undefined, fieldValue: string, index: number, productSpecificCandidate: string) => {
    const isManuallyValidated = getValues?.(`thirdPartyReferenceValidated-${index}`) === true;

    if (isManuallyValidated || confidence === undefined || (fieldValue && fieldValue !== productSpecificCandidate)) {
      return "";
    }
    if (confidence < 25) return "border-red-500 border-2";
    if (confidence < 75) return "border-orange-300 border-2";
    return "";
  }, [getValues]);

  const getStatusIcon = useCallback((product: any, productID: string) => {
    const referenceSource = product.referenceSource;
    const currentFieldValue = getValues?.(`thirdPartyReference-${product.index}`) || "";
    const isValidated = getValues?.(`thirdPartyReferenceValidated-${product.index}`) === true;
    const hasReference = currentFieldValue && currentFieldValue.trim() !== "";

    let initialValue = "";
    if (referenceSource === "hitl" || referenceSource === "bdd") {
      const apiEntry = specificProductsIndex.get(productID);
      initialValue = apiEntry?.value || "";
    } else {
      initialValue = product.productSpecificCandidate || "";
    }

    const hasBeenModified = currentFieldValue !== "" && currentFieldValue !== initialValue;

    if (referenceSource !== "bdd" && (isValidated || hasBeenModified)) {
      return <GiSandsOfTime className="fill-vistawarningblue text-vistawarningblue text-2xl" />;
    }

    if (referenceSource === "bdd" || referenceSource === "hitl") {
      return <PiCheckSquareFill className="fill-green-500 text-green-500 text-2xl" />;
    }

    if (!hasReference) {
      return <CgUnavailable className="fill-red-500 text-red-500 text-2xl" />;
    }

    return <PiWarningFill className="fill-orange-500 text-orange-500 text-2xl" />;
  }, [getValues, specificProductsIndex]);

  const getTooltipContent = useCallback((product: any, valueConfidence?: number) => {
    const rawSource = product.referenceSource || "candidate";

    const sourceLabels: Record<string, string> = {
      bdd: "ERP",
      hitl: t("hitl_vistaflow"),
      candidate: t("document_detection")
    };

    const source = sourceLabels[rawSource] || rawSource;
    return `Source: ${source}`;
  }, []);

  const handleInputChange = useCallback((fieldOnChange: (e: any) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      fieldOnChange(e);
      setRenderKey(prev => prev + 1);
    };
  }, []);

  const columns = useMemo(
    () => [
      {
        name: t("status"),
        uid: "status",
        sortable: false,
        style: { maxWidth: "40px" },
      },
      {
        name: t("product_name"),
        uid: "productName",
        sortable: false,
        style: { minWidth: "250px" },
      },
      {
        name: t("product_id"),
        uid: "productId",
        sortable: false,
        style: { minWidth: "150px" },
      },
      {
        name: t("third_party_reference"),
        uid: "reference",
        sortable: false,
        style: { minWidth: "200px" },
      },
      {
        name: t("actions"),
        uid: "actions",
        sortable: false,
        style: { maxWidth: "90px" },
      },
    ],
    [t]
  );

  const items = useMemo(() => {
    if (!data?.order?.products) return [];

    const productsWithIndex = data.order.products.map((product, index) => ({
      ...product,
      index,
    }));

    const filteredProducts = productsWithIndex.filter(
      (product) => product.product_pageNumber?.value === currentPage
    );

    return filteredProducts.map((product) => {
      const productId = product.product_id?.value || "";
      return {
        index: product.index,
        productID: productId,
        productName: product.product_name?.value || "",
        productSpecificCandidate: product.product_specific_candidate?.value || "",
        productSpecificCandidateConfidence: product.product_specific_candidate?.valueConfidence,
        referenceSource: getReferenceSource(productId),
      };
    });
  }, [data, currentPage, getReferenceSource]);

  const validateReference = useCallback((index: number) => {
    setValue(`thirdPartyReferenceValidated-${index}`, true);
    setRenderKey(prev => prev + 1);
  }, [setValue]);

  const getInitialReferenceValue = useCallback((productId: string, product: Product, referenceSource?: string) => {
    if (referenceSource === "hitl" || referenceSource === "bdd") {
      const apiEntry = specificProductsIndex.get(productId);
      return apiEntry?.value || "";
    }
    return product.product_specific_candidate?.value || "";
  }, [specificProductsIndex]);

  const shouldIncludeProduct = useCallback((
    referenceSource: string | undefined,
    fieldValue: string,
    initialValue: string,
    isManuallyValidated: boolean
  ) => {
    const isFromBDD = referenceSource === "bdd";
    const isFromHITL = referenceSource === "hitl";
    const hasBeenModified = fieldValue !== "" && fieldValue !== initialValue;

    if (isFromBDD) return false;

    if (isFromHITL && !hasBeenModified) return false;

    if (!isFromHITL && !isManuallyValidated && !hasBeenModified) return false;

    return true;
  }, []);

  const formatReferencesForAPI = useCallback(() => {
    if (!data?.order?.products) return null;

    const apiData: Array<{
      referential_id_specific: string;
      product_id: string;
      product_id_specific: string;
      source?: string;
    }> = [];

    for (let index = 0; index < data.order.products.length; index++) {
      const product = data.order.products[index];
      const productId = product.product_id?.value || "";
      const fieldValue = getValues?.(`thirdPartyReference-${index}`);
      const isManuallyValidated = getValues?.(`thirdPartyReferenceValidated-${index}`) === true;
      const referenceSource = getReferenceSource(productId);

      const initialValue = getInitialReferenceValue(productId, product, referenceSource);

      if (!shouldIncludeProduct(referenceSource, fieldValue, initialValue, isManuallyValidated)) {
        continue;
      }

      if (fieldValue && fieldValue.trim() !== "" && productId) {
        apiData.push({
          referential_id_specific: customerId,
          product_id: productId,
          product_id_specific: fieldValue.trim(),
        });
      }
    }

    if (apiData.length === 0) {
      return null;
    }

    return {
      operation: "insert",
      delete_before: false,
      data: apiData,
    };
  }, [data, getValues, getReferenceSource, customerId, getInitialReferenceValue, shouldIncludeProduct]);

  const handleSubmit = async () => {
    const apiPayload = formatReferencesForAPI();

    if (!apiPayload || apiPayload.data.length === 0) {
      if (onSubmit) {
        onSubmit({ operation: "insert", delete_before: false, data: [] });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axiosPrivate.post("/products/specific/bulk", apiPayload);

      if (response.status === 200 && response.data?.message === "inserted") {
        for (const item of apiPayload.data) {
          const productIndex = data?.order?.products?.findIndex(
            (p) => p.product_id?.value === item.product_id
          );
          if (productIndex !== undefined && productIndex !== -1) {
            setValue(`thirdPartyReferenceValidated-${productIndex}`, false);
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["specificProducts", customerId] });

      if (onSubmit) {
        onSubmit(apiPayload);
      }
    } catch (error) {
      console.error("Error submitting references:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCell = React.useCallback(
    (product: any, columnKey: string) => {
      const productID = product.productID || "";
      const productName = product.productName || "";
      const valueConfidence = product.productSpecificCandidateConfidence;

      let cellClass = "";
      if (columnKey === "status") {
        cellClass = "w-[50px] bg-inherit";
      } else if (columnKey === "productId") {
        cellClass = "bg-inherit";
      } else if (columnKey === "productName") {
        cellClass = "min-w-[250px] bg-inherit";
      } else if (columnKey === "reference") {
        cellClass = "min-w-[100px] max-w-[120px] bg-inherit";
      } else if (columnKey === "actions") {
        cellClass = "w-[70px] bg-inherit";
      }

      switch (columnKey) {
        case "status":
          return (
            <TableCell className={cellClass}>
              <Tooltip content={getTooltipContent(product, valueConfidence)}>
                <div className="flex items-center mt-5 justify-center">
                  {getStatusIcon(product, productID)}
                </div>
              </Tooltip>
            </TableCell>
          );
        case "productId":
          return (
            <TableCell className={cellClass}>
              <div className="text-sm font-medium">{productID}</div>
            </TableCell>
          );
        case "productName":
          return (
            <TableCell className={cellClass}>
              <div className="text-xs text-vistagrey">{product.index + 1} / {productName}</div>
            </TableCell>
          );
        case "reference": {
          const isFromBDD = product.referenceSource === "bdd";
          const isFromHITL = product.referenceSource === "hitl";
          return (
            <TableCell className={cellClass}>
              <Controller
                name={`thirdPartyReference-${product.index}`}
                control={control}
                defaultValue=""
                render={({ field }) => {
                  const borderClass = (isFromBDD || isFromHITL) ? "" : getBorderClass(valueConfidence, field.value, product.index, product.productSpecificCandidate);
                  const bddClasses = isFromBDD ? "!bg-transparent !shadow-none" : "";
                  return (
                    <Input
                      {...field}
                      onChange={handleInputChange(field.onChange)}
                  type="text"
                      placeholder={t("enter_reference")}
                      size="sm"
                      readOnly={isFromBDD}
                      classNames={{
                        input: "text-sm",
                        inputWrapper: `h-10 ${borderClass} ${bddClasses}`,
                      }}
                    />
                  );
                }}
              />
            </TableCell>
          );
        }
        case "actions": {
          const isValidated = getValues?.(`thirdPartyReferenceValidated-${product.index}`) === true;
          const currentFieldValue = getValues?.(`thirdPartyReference-${product.index}`) || "";
          const referenceSource = product.referenceSource;

          let initialValue = "";
          if (referenceSource === "hitl" || referenceSource === "bdd") {
            const apiEntry = specificProductsIndex.get(productID);
            initialValue = apiEntry?.value || "";
          } else {
            initialValue = product.productSpecificCandidate || "";
          }

          const hasBeenModified = currentFieldValue !== "" && currentFieldValue !== initialValue;

          const isCandidate = !referenceSource; 
          const shouldShowButton = isCandidate && !isValidated && !hasBeenModified;

          return (
            <TableCell className={cellClass}>
              {shouldShowButton && (
                <button
                  className="flex items-center p-2 rounded-full text-white bg-vistagreen/10 hover:bg-vistagreen/20 cursor-pointer mx-1 transition-colors"
                  data-cy={`order__validateReferenceSelection-${product.index}`}
                  tabIndex={-1}
                  onClick={(event) => {
                    event.preventDefault();
                    validateReference(product.index);
                  }}
                >
                  <IoIosCheckmarkCircle className="fill-vistagreen/70 hover:fill-vistagreen h-6 w-6 transition-colors" />
                </button>
              )}
            </TableCell>
          );
        }
        default:
          return <TableCell>{""}</TableCell>;
      }
    },
    [control, t, validateReference, getValues, renderKey, specificProductsIndex, getStatusIcon, getTooltipContent, getBorderClass, handleInputChange]
  );

  const classNames = React.useMemo(
      () => ({
        wrapper: ["max-h-[382px]", "max-w-3xl"],
        table: ["border-separate", "border-spacing-y-2"],
        th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
        td: [
          "bg-white",
          "2",
          "group-data-[first=true]:first:before:rounded-none",
          "group-data-[first=true]:last:before:rounded-none",
          "group-data-[middle=true]:before:rounded-none",
          "group-data-[last=true]:first:before:rounded-none",
          "group-data-[last=true]:last:before:rounded-none",
        ],
      }),
      [],
    );

  if (isLoadingSpecificProducts || !isInitialized) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vistagreen"></div>
          <p className="text-sm text-vistagrey">{t("loading")}...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
        className="border-b-1 border-vistagrey/30 px-3 pt-1 pb-3 text-xs font-bold"
        data-cy="order__productsNumber"
      >
        {t("product_list")} ({data?.order?.products?.length || 0})
      </div>
      <Table
        isCompact
        removeWrapper
        aria-label="third_party_references"
        checkboxesProps={{
          classNames: {
            wrapper:
              "after:bg-foreground after:text-background text-background",
          },
        }}
        classNames={classNames}
        topContentPlacement="outside"
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              style={column.style}
              className="bg-vistagreybg text-vistagrey text-xs font-semibold"
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody emptyContent={t("no_order")} items={items}>
          {items.map((item) => (
            <TableRow
              key={`product-${item.index}`}
              className={
                hoveredShapeDraw?.action === "mouseEnter" &&
                hoveredShapeDraw?.canvasId === `${item.index}`
                  ? "bg-vistagrey/20"
                  : "bg-white"
              }
              onMouseEnter={() => setHoveredShapeId("mouseEnter", item.index)}
              onMouseLeave={() => setHoveredShapeId("mouseLeave", item.index)}
            >
              {(columnKey) => renderCell(item, columnKey as string)}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex justify-center">
        <Button
          color="primary"
          onPress={handleSubmit}
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          data-cy="submit-third-party-references"
        >
          {t("submit_third_party_references")}
        </Button>
      </div>
    </>
  );
}
